import { useEffect, useState } from 'react';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { useTheme } from '@/hooks/useTheme';
import { getStoredUser } from '@/hooks/useAuth';
import { API_BASE_URL, authFetch } from '@/services/api';

interface Station {
  id: string; name: string; installedMw: number; isPreLaunch: boolean;
  today: { forecastMwh: number | null; effPriceUah: number | null; revenueUah: number; hours: number };
  mtd: { forecastMwh: number | null; effPriceUah: number | null; revenueUah: number };
  lossMtd: { events: number; lostMwh: number | null; lostUah: number; kinds: string | null };
}
interface PriceIdx { base: number | null; lo: number | null; hi: number | null; n: number }
interface MathData {
  generatedAt: string; contractKeepPct: number; keep: number;
  sources: { forecast: string; price: string; loss: string };
  rdnBaseIndex: { today: PriceIdx | null; mtd: PriceIdx | null };
  stations: Station[];
  totals: { todayForecastMwh: number; todayRevenueUah: number; mtdForecastMwh: number; mtdRevenueUah: number; mtdLostMwh: number; mtdLostUah: number };
}

const short = (n: string) => n.replace(/^СЕС\s+/, '').trim();
const num = (n: number | null | undefined, d = 0) =>
  n == null ? '—' : Number(n).toLocaleString('uk-UA', { minimumFractionDigits: d, maximumFractionDigits: d });

export default function MathDashPage() {
  const { isDark } = useTheme();
  const user = getStoredUser();
  const [data, setData] = useState<MathData | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    authFetch(`${API_BASE_URL}/owner/mathdash`)
      .then((r) => (r.ok ? r.json() : Promise.reject(`HTTP ${r.status}`)))
      .then(setData)
      .catch((e) => setErr(String(e)))
      .finally(() => setLoading(false));
  }, []);

  const cardBg = isDark ? '#1A1A1A' : '#FFFFFF';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = '#6B6B70';
  const textDim = isDark ? '#4A4A50' : '#9B9BA4';
  const rowBorder = isDark ? 'rgba(255,255,255,0.06)' : '#F0F0F2';
  const codeBg = isDark ? '#0F0F0F' : '#F5F5F7';
  const headBg = isDark ? 'rgba(255,255,255,0.03)' : '#FAFAFB';

  const Card = ({ title, sub, children }: { title?: string; sub?: string; children: React.ReactNode }) => (
    <div className="rounded-xl mb-4" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
      {title && (
        <div className="px-4 py-3" style={{ borderBottom: `1px solid ${cardBorder}` }}>
          <div className="text-sm font-semibold" style={{ color: textMain }}>{title}</div>
          {sub && <div className="text-[11px] mt-0.5" style={{ color: textMuted }}>{sub}</div>}
        </div>
      )}
      <div className="p-4">{children}</div>
    </div>
  );

  const Formula = ({ children }: { children: React.ReactNode }) => (
    <div className="text-[12px] rounded-lg px-3 py-2 mb-3 font-mono" style={{ background: codeBg, color: textMain, border: `1px solid ${cardBorder}`, overflowX: 'auto' }}>
      {children}
    </div>
  );

  const th = "text-left text-[10px] font-bold uppercase tracking-wider px-2 py-2";
  const td = "px-2 py-1.5 text-[12px] tabular-nums";

  const live = (data?.stations ?? []).filter((s) => !s.isPreLaunch);

  return (
    <DashboardLayout breadcrumb={['MathDash']}>
      <div style={{ color: textMain }}>
        <div className="mb-6">
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Inter, sans-serif' }}>MathDash — перевірка формул дашборду</h1>
          <p className="text-sm mt-1" style={{ color: textMuted }}>
            Технічний розклад обчислень для акаунту{user ? ` · ${user.displayName}` : ''}. Формули, джерела даних і розподіл по кожній СЕС портфелю.
            {data && <span style={{ color: textDim }}> · згенеровано {new Date(data.generatedAt).toLocaleString('uk-UA')}</span>}
          </p>
        </div>

        {loading && <Card><span style={{ color: textMuted }}>Завантаження…</span></Card>}
        {err && <Card><span style={{ color: 'var(--accent)' }}>Помилка: {err}</span></Card>}

        {data && (
          <>
            {/* Sources & constants */}
            <Card title="Джерела даних і константи">
              <div className="space-y-2 text-[12px]" style={{ color: textMain }}>
                <div><b>Прогноз:</b> <span style={{ color: textMuted }}>{data.sources.forecast}</span></div>
                <div><b>Ціна РДН:</b> <span style={{ color: textMuted }}>{data.sources.price}</span></div>
                <div><b>Втрати:</b> <span style={{ color: textMuted }}>{data.sources.loss}</span></div>
                <div><b>Контракт:</b> <span style={{ color: textMuted }}>РДН −{data.contractKeepPct}% → множник KEEP = {data.keep}</span></div>
              </div>
            </Card>

            {/* RDN Base index */}
            <Card title="Індекс РДН (DAM) — довідково" sub="Дашборд рахує дохід за ПОГОДИННОЮ ціною (точніше); Base-індекс (середня за добу) показано для звірки.">
              <div className="grid grid-cols-2 gap-3 text-[12px]">
                {(['today', 'mtd'] as const).map((k) => {
                  const p = data.rdnBaseIndex[k];
                  return (
                    <div key={k} className="rounded-lg px-3 py-2" style={{ background: codeBg, border: `1px solid ${cardBorder}` }}>
                      <div className="text-[10px] uppercase font-bold" style={{ color: textMuted }}>{k === 'today' ? 'Сьогодні' : 'З початку місяця'}</div>
                      <div className="text-base font-bold tabular-nums" style={{ color: textMain }}>{num(p?.base ?? null, 0)} <span className="text-[11px] font-normal" style={{ color: textMuted }}>грн/МВт·год</span></div>
                      <div className="text-[10px]" style={{ color: textDim }}>діапазон {num(p?.lo ?? null)}–{num(p?.hi ?? null)} · {p?.n ?? 0} год</div>
                    </div>
                  );
                })}
              </div>
            </Card>

            {/* Algorithm 1 */}
            <Card title="Алгоритм 1 — ідеальний дохід (прогноз × РДН)" sub="За ідеальних умов: вся прогнозна генерація продана на РДН.">
              <Formula>Дохід<sub>СЕС</sub> = Σ<sub>год</sub> ( прогноз_кВт·год / 1000 × ціна_РДН[год] грн/МВт·год × {data.keep} )</Formula>
              <div className="overflow-x-auto">
                <table className="w-full" style={{ borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: headBg, color: textMuted }}>
                      <th className={th}>СЕС</th>
                      <th className={th} style={{ textAlign: 'right' }}>Прогноз сьогодні, МВт·год</th>
                      <th className={th} style={{ textAlign: 'right' }}>Сер. ціна, грн/МВт·год</th>
                      <th className={th} style={{ textAlign: 'right' }}>Дохід сьогодні, грн</th>
                      <th className={th} style={{ textAlign: 'right' }}>Прогноз MTD, МВт·год</th>
                      <th className={th} style={{ textAlign: 'right' }}>Дохід MTD, грн</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.stations.map((s) => (
                      <tr key={s.id} style={{ borderBottom: `1px solid ${rowBorder}`, color: s.isPreLaunch ? textDim : textMain }}>
                        <td className={td}>{short(s.name)}{s.isPreLaunch && <span className="text-[10px]" style={{ color: textDim }}> · не запущено</span>}</td>
                        <td className={td} style={{ textAlign: 'right' }}>{num(s.today.forecastMwh, 2)}</td>
                        <td className={td} style={{ textAlign: 'right', color: textMuted }}>{num(s.today.effPriceUah)}</td>
                        <td className={td} style={{ textAlign: 'right', fontWeight: 600, color: '#0D7A3C' }}>{num(s.today.revenueUah)}</td>
                        <td className={td} style={{ textAlign: 'right' }}>{num(s.mtd.forecastMwh, 1)}</td>
                        <td className={td} style={{ textAlign: 'right', fontWeight: 600, color: '#E0A030' }}>{num(s.mtd.revenueUah)}</td>
                      </tr>
                    ))}
                    <tr style={{ borderTop: `2px solid ${cardBorder}`, fontWeight: 700 }}>
                      <td className={td}>Σ портфель</td>
                      <td className={td} style={{ textAlign: 'right' }}>{num(data.totals.todayForecastMwh, 1)}</td>
                      <td className={td}></td>
                      <td className={td} style={{ textAlign: 'right', color: '#0D7A3C' }}>{num(data.totals.todayRevenueUah)}</td>
                      <td className={td} style={{ textAlign: 'right' }}>{num(data.totals.mtdForecastMwh, 1)}</td>
                      <td className={td} style={{ textAlign: 'right', color: '#E0A030' }}>{num(data.totals.mtdRevenueUah)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card>

            {/* Algorithm 2 */}
            <Card title="Алгоритм 2 — втрати від обмежень/відключень (з початку місяця)" sub="СКО / ОСР / аварійні відключення: недовиробіток × ціна РДН.">
              <Formula>Втрати<sub>СЕС</sub> = Σ<sub>год події</sub> ( прогноз_кВт·год / 1000 × ціна_РДН[год] × {data.keep} × частка_обмеження )</Formula>
              <div className="overflow-x-auto">
                <table className="w-full" style={{ borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: headBg, color: textMuted }}>
                      <th className={th}>СЕС</th>
                      <th className={th} style={{ textAlign: 'right' }}>Подій</th>
                      <th className={th} style={{ textAlign: 'right' }}>Недовироблено, МВт·год</th>
                      <th className={th} style={{ textAlign: 'right' }}>Втрати, грн</th>
                      <th className={th}>Типи</th>
                    </tr>
                  </thead>
                  <tbody>
                    {live.map((s) => (
                      <tr key={s.id} style={{ borderBottom: `1px solid ${rowBorder}` }}>
                        <td className={td}>{short(s.name)}</td>
                        <td className={td} style={{ textAlign: 'right', color: textMuted }}>{s.lossMtd.events || '—'}</td>
                        <td className={td} style={{ textAlign: 'right' }}>{s.lossMtd.lostMwh ? num(s.lossMtd.lostMwh, 2) : '—'}</td>
                        <td className={td} style={{ textAlign: 'right', fontWeight: 600, color: s.lossMtd.lostUah ? 'var(--accent)' : textDim }}>{s.lossMtd.lostUah ? `−${num(s.lossMtd.lostUah)}` : '—'}</td>
                        <td className={td} style={{ color: textDim }}>{s.lossMtd.kinds || '—'}</td>
                      </tr>
                    ))}
                    <tr style={{ borderTop: `2px solid ${cardBorder}`, fontWeight: 700 }}>
                      <td className={td}>Σ портфель</td>
                      <td className={td}></td>
                      <td className={td} style={{ textAlign: 'right' }}>{num(data.totals.mtdLostMwh, 2)}</td>
                      <td className={td} style={{ textAlign: 'right', color: 'var(--accent)' }}>−{num(data.totals.mtdLostUah)}</td>
                      <td className={td}></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card>

            {/* Cross-check with the dashboard tiles */}
            <Card title="Звірка з плитками дашборду власника" sub="Підсумки нижче мають збігатися з відповідними плитками на /dashboard/owner.">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { label: 'Прогноз на сьогодні', val: `${num(data.totals.todayForecastMwh, 1)} МВт·год`, color: '#1F5B8F' },
                  { label: 'Дохід сьогодні', val: `${num(data.totals.todayRevenueUah)} грн`, color: '#0D7A3C' },
                  { label: 'Дохід з початку місяця', val: `${num(data.totals.mtdRevenueUah)} грн`, color: '#E0A030' },
                ].map((x) => (
                  <div key={x.label} className="rounded-lg px-3 py-2" style={{ background: codeBg, border: `1px solid ${cardBorder}` }}>
                    <div className="text-[10px] uppercase font-bold tracking-wider" style={{ color: textMuted }}>{x.label}</div>
                    <div className="text-base font-bold tabular-nums" style={{ color: x.color }}>{x.val}</div>
                  </div>
                ))}
              </div>
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
